import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Image,
  Drawer,
  Divider,
  Flex,
  Typography,
  Popover,
  List,
  Badge,
  Select,
  message,
} from "antd";
import {
  MenuOutlined,
  UserOutlined,
  LockOutlined,
  LogoutOutlined,
  RightOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  CopyOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import Logo from "../../assets/images/logo.png";
import SidebarContent from "../Sidebar/SidebarContent";
import CustomButton from "../CustomButton/Button";
import walletBlackIcon from "../../assets/icons/wallet-black.svg";
import userBlackIcon from "../../assets/icons/user-black.svg";
import notificationBlackIcon from "../../assets/icons/notification-black.svg";
import supportBlackIcon from "../../assets/icons/support-black.svg";
import UserSquareIcon from "../../assets/icons/user-square.svg";
import CallIcon from "../../assets/icons/call.svg";
import MailIcon from "../../assets/icons/sms.svg";
import AccountDetails from "../AccountDetails/AccountDetails";
import CustomModal from "../CustomModal/CustomModal";
import { useDispatch, useSelector } from "react-redux";
import NoData from "../NoData/NoData";
import { getBalanceByOrgId } from "../../redux/slices/manageFundSlice";
import { count, logout } from "../../redux/slices/AuthSlice";
import { formatCurrencyTwoDec, formatTimeAgo } from "../../helpers/helpers";
import ArrowDownIcon from "../../assets/icons/arrow-down-icon.svg";
import {
  BTN_TXT,
  DESC_TXT,
  GLOBAL_CONSTANTS,
  TITLE_TXT,
} from "../../constants/constants";
import { useSocket } from "../../hooks/useSocket";
import {
  markNotificationAsRead,
  notifyMarkAsRead,
} from "../../redux/slices/NotifySlice";
import { setActiveCategory } from "../../redux/slices/dealCategorySlice";
import {
  resetOnboardingStatus,
  resetQuestions,
} from "../../redux/slices/QuestionnaireSlice";
import {
  isVenturesCategory,
  getDealCategoryDisplayName,
  isCategoryRestricted,
  RESTRICTED_STATUS_LABELS,
} from "../../helpers/dealCategoryProducts";
import "./Navbar.scss";

const { Header } = Layout;
const { Text, Paragraph, Title, Link } = Typography;

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { accBalance } = useSelector((state) => state.manageFunds);
  const { notifyList, unreadCount } = useSelector(
    (state) => state.notification,
  );
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [notifPopoverOpen, setNotifPopoverOpen] = useState(false);

  const dealCategoryId =
    activeCategory?.dealCategoryId || user?.dealCategories?.[0]?.dealCategoryId;
  const currentCategoryName = getDealCategoryDisplayName(
    dealCategoryId,
    activeCategory?.dealCategoryName || user?.dealCategories?.[0]?.dealCategoryName,
  );
  const isJV = isVenturesCategory(dealCategoryId);
  const showWallet =
    activeCategory?.statusId === "USM1000001" && !isJV;

  useSocket(user?.userId);

  // Listen for window resize to determine if the screen is mobile size
  useEffect(() => {
    if (showWallet) {
      // superman
      const fetchAccountBalance = async () => {
        const res = await dispatch(getBalanceByOrgId(user?.orgId));

        if (res?.payload?.amount) {
          const amount = res.payload.amount;

          const updatedamount = { ...user, amount: amount };
          localStorage.setItem("busUser", JSON.stringify(updatedamount));
        }
      };
      fetchAccountBalance();
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Mobile if screen width is less than or equal to 768px
    };

    handleResize(); // Call once on mount
    window.addEventListener("resize", handleResize); // Add resize listener

    return () => window.removeEventListener("resize", handleResize); // Cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showWallet]);

  const handleSidebarDrawerClose = () => {
    setSidebarDrawerOpen(false);
  };

  const navigateToProfile = (value) => {
    navigate("/profile", { state: { value } });
    setProfilePopoverOpen(false);
  };

  const handleLogout = async () => {
    const res = await dispatch(logout(user?.userId));
    if (res?.payload?.statusCode === 201) {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const markAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId)); // instantly update local state
    dispatch(notifyMarkAsRead({ notificationId, payload: { isRead: true } }));
  };

  const notifPopoverContent = (
    <div className="notif-popover-container">
      <Title className="notif-title" level={5}>Notifications</Title>
      <List
        itemLayout="horizontal"
        dataSource={notifyList.slice(0, 3)}
        locale={{ emptyText: "No notifications" }}
        renderItem={(item) => (
          <List.Item
            style={{
              backgroundColor: item.isRead ? "" : "#F5F8FF",
              cursor: "pointer",
              transition: "0.2s",
            }}
            onClick={() => {
              markAsRead(item.notificationId);
              navigate("/notifications");
              setNotifPopoverOpen(false);
            }}
          >
            <List.Item.Meta
              title={
                <Flex justify="space-between" align="center" gap={8}>
                  <Text ellipsis style={{ fontSize: 14, flex: 1, minWidth: 0 }}>
                    {item.message}
                  </Text>
                  <Text style={{ color: "#768EA7", fontSize: 10, flexShrink: 0 }}>
                    {formatTimeAgo(item.createdAt)}
                  </Text>
                </Flex>
              }
              description={
                item.redirectUrl ? (
                  <Text style={{ fontSize: 12, color: "#768EA7" }}>
                    <Link
                      style={{ fontSize: 12, textDecoration: "underline" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(item.notificationId);
                        navigate(item.redirectUrl);
                        setNotifPopoverOpen(false);
                      }}
                    >
                      {BTN_TXT.CLICK_HERE}
                    </Link>
                    {` ${BTN_TXT.TO_VIEW_DETAILS}`}
                  </Text>
                ) : (
                  <Paragraph style={{ fontSize: 12, marginBottom: 0 }} ellipsis={{ rows: 1 }}>
                    {item.description}
                  </Paragraph>
                )
              }
            />
          </List.Item>
        )}
      />
      {notifyList.length > 3 && (
        <Flex justify="center" style={{ padding: "20px 12px 12px" }}>
          <Link
            style={{ fontSize: 12 }}
            onClick={() => {
              navigate("/notifications");
              setNotifPopoverOpen(false);
            }}
          >
            {BTN_TXT.VIEW_ALL}
          </Link>
        </Flex>
      )}
    </div>
  );

  const copyToClipboard = (e, text, label) => {
    e.stopPropagation();
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      message?.success?.(`${label} copied!`);
    }
  };

  const profilePopoverContent = (
    <div className="profile-popover-card">
      {/* User Info Header */}
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          <div className="profile-avatar" data-testid="profile-avatar">
            {getInitials(user?.userName)}
          </div>
          <span className="profile-status-dot" />
        </div>
        <div className="profile-meta">
          <Text className="profile-name">{user?.userName}</Text>
          <span className="profile-role-badge">
            <SafetyCertificateOutlined className="role-icon" />
            <Text className="profile-role-text">Business</Text>
          </span>
        </div>
      </div>

      {user?.email && (
        <div className="profile-email-row">
          <MailOutlined className="email-icon" />
          <Text className="profile-email" ellipsis>{user?.email}</Text>
        </div>
      )}

      {/* Category / Deal Type Switcher */}
      {user?.dealCategories && user.dealCategories.length > 0 && (
        <div className="profile-category-switcher">
          <div className="category-switcher-header">
            <SwapOutlined className="switcher-icon" />
            <Text className="switcher-label">Deal Type</Text>
          </div>
          <Select
            size="middle"
            value={activeCategory?.dealCategoryId}
            placeholder="Select Category"
            className="category-select"
            suffixIcon={
              <Image src={ArrowDownIcon} alt="Arrow Down" preview={false} />
            }
            onChange={(value) => {
              const selected = user.dealCategories.find((c) => c.dealCategoryId === value);
              if (selected && isCategoryRestricted(selected.statusId)) {
                return;
              }
              dispatch(setActiveCategory(selected));
              dispatch(resetOnboardingStatus());
              dispatch(resetQuestions());
              dispatch(count({ type: "ALL", dealCategoryId: value }));
              navigate("/questionnaire");
            }}
            options={user?.dealCategories?.map((cat) => {
              const restricted = isCategoryRestricted(cat.statusId);
              const statusLabel = RESTRICTED_STATUS_LABELS[cat.statusId];
              const label = restricted && statusLabel
                ? `${cat.dealCategoryName} (${statusLabel})`
                : cat.dealCategoryName;
              return {
                label,
                value: cat.dealCategoryId,
                disabled: restricted,
              };
            })}
          />
        </div>
      )}

      <div className="profile-divider" />

      {/* Relationship Manager Section */}
      <div className="rm-card-wrapper">
        <div className="rm-card-header">
          <Text className="rm-title-primary">Relationship Manager</Text>
        </div>

        {user?.RMuser?.rmId ? (
          <div className="rm-contact-list">
            {user?.RMuser?.UserDetailsView?.userName && (
              <div className="rm-contact-item rm-user-row">
                <span className="rm-item-icon-wrapper">
                  <UserOutlined className="rm-item-icon" />
                </span>
                <Text className="rm-contact-text rm-user-name">
                  {user?.RMuser?.UserDetailsView?.userName}
                </Text>
              </div>
            )}

            {user?.RMuser?.UserDetailsView?.mobile && (
              <div className="rm-contact-item">
                <span className="rm-item-icon-wrapper">
                  <PhoneOutlined className="rm-item-icon" />
                </span>
                <a
                  href={`tel:${user?.RMuser?.UserDetailsView?.mobile}`}
                  className="rm-contact-link"
                >
                  {user?.RMuser?.UserDetailsView?.mobile}
                </a>
                <button
                  type="button"
                  className="rm-copy-btn"
                  title="Copy Phone"
                  onClick={(e) => copyToClipboard(e, user?.RMuser?.UserDetailsView?.mobile, "Phone number")}
                >
                  <CopyOutlined />
                </button>
              </div>
            )}

            {user?.RMuser?.UserDetailsView?.email && (
              <div className="rm-contact-item">
                <span className="rm-item-icon-wrapper">
                  <MailOutlined className="rm-item-icon" />
                </span>
                <a
                  href={`mailto:${user?.RMuser?.UserDetailsView?.email}`}
                  className="rm-contact-link"
                >
                  {user?.RMuser?.UserDetailsView?.email}
                </a>
                <button
                  type="button"
                  className="rm-copy-btn"
                  title="Copy Email"
                  onClick={(e) => copyToClipboard(e, user?.RMuser?.UserDetailsView?.email, "Email")}
                >
                  <CopyOutlined />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rm-empty-state">
            <NoData desc={DESC_TXT.RM_DET_NOT_AVAIL} />
          </div>
        )}
      </div>

      <div className="profile-divider" />

      {/* Navigation Action Items */}
      <div className="profile-menu-items">
        <CustomButton
          type="text"
          className="profile-menu-btn"
          icon={<UserOutlined className="menu-leading-icon" />}
          onClick={() => navigateToProfile("personal")}
        >
          <span className="menu-btn-content">
            <span className="menu-label">{BTN_TXT.PROFILE}</span>
            <RightOutlined className="menu-trailing-chevron" />
          </span>
        </CustomButton>

        <CustomButton
          type="text"
          className="profile-menu-btn"
          icon={<LockOutlined className="menu-leading-icon" />}
          onClick={() => navigateToProfile("password")}
        >
          <span className="menu-btn-content">
            <span className="menu-label">{BTN_TXT.CHNG_PWD}</span>
            <RightOutlined className="menu-trailing-chevron" />
          </span>
        </CustomButton>
      </div>

      <div className="profile-divider" />

      {/* Logout Action */}
      <div className="profile-footer-actions">
        <CustomButton
          variant="text"
          color="danger"
          className="profile-menu-btn logout-action-btn"
          icon={<LogoutOutlined className="logout-leading-icon" />}
          onClick={handleLogout}
        >
          <span className="menu-label">{BTN_TXT.LOGOUT}</span>
        </CustomButton>
      </div>
    </div>
  );

  return (
    <Header className="header">
      <div className="navbar-left">
        <CustomButton
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setSidebarDrawerOpen(true)}
          className="menu-button"
        />
        <Drawer
          title={null}
          placement="left"
          onClose={handleSidebarDrawerClose}
          open={sidebarDrawerOpen}
          width="70%"
          style={{ background: "#DB5A00" }}
          className="custom-drawer"
        >
          <SidebarContent onClose={handleSidebarDrawerClose} />
        </Drawer>
        <img src={Logo} alt="logo" className="logo" />
        {currentCategoryName && (
          <div className={`financing-type-indicator ${dealCategoryId === "DCT1000006" ? "is-supply-chain" : "is-invoice"}`}>
            <span className="indicator-icon-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="indicator-dot" />
            <Text strong className="indicator-text">
              {currentCategoryName}
            </Text>
          </div>
        )}
      </div>
      <div className="navbar-right">
        {showWallet && (
          <>
            <Image src={walletBlackIcon} alt="wallet" preview={false} />
            <Flex vertical>
              <Text className="wallet-balance">
                {formatCurrencyTwoDec(accBalance?.amount || "0")}
              </Text>
              {accBalance?.amount > GLOBAL_CONSTANTS.MIN_BAL_TO_BUY_DEAL ? (
                <Text className="wallet-balance-text">
                  {GLOBAL_CONSTANTS.WAL_BAL}
                </Text>
              ) : (
                <>
                  <CustomButton
                    type="text"
                    className="add-money-btn"
                    onClick={() => setAddBalanceOpen(true)}
                  >
                    {BTN_TXT.ADD_MON}
                  </CustomButton>
                  <CustomModal
                    open={addBalanceOpen}
                    centered
                    onCancel={() => setAddBalanceOpen(false)}
                    content={<AccountDetails title={TITLE_TXT.ECW_DET_TXT} />}
                    width={650}
                  />
                </>
              )}
            </Flex>
            <Divider type="vertical" className="divider" />
          </>
        )}
        <Popover
          content={notifPopoverContent}
          trigger="click"
          placement="bottom"
          open={notifPopoverOpen}
          styles={{ body: { padding: 0 } }}
          onOpenChange={(value) => setNotifPopoverOpen(value)}
        >
          <Badge count={unreadCount} overflowCount={9} size="small">
            <Image
              src={notificationBlackIcon}
              alt="notification"
              preview={false}
            />
          </Badge>
        </Popover>

        {!isMobile && (
          <Image
            src={supportBlackIcon}
            alt="support"
            preview={false}
            onClick={() => navigate("/support")}
          />
        )}

        <Popover
          content={profilePopoverContent}
          trigger="click"
          placement="bottomRight"
          open={profilePopoverOpen}
          overlayClassName="profile-dropdown-popover"
          overlayInnerStyle={{ padding: 0 }}
          onOpenChange={(value) => setProfilePopoverOpen(value)}
        >
          <div
            className={`profile-trigger-avatar ${profilePopoverOpen ? 'active' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="User Profile"
          >
            <Image src={userBlackIcon} alt="user" preview={false} />
          </div>
        </Popover>
      </div>
    </Header>
  );
};

export default Navbar;
